import type {
  ProductTypeCategoryDocument,
  SiteAwareProductTypeCategoryDocument,
} from '@/types/sanity'
import type {SiteLite} from '@/multisite/types'
import type {CanonicalSettingsDocument} from '@/types/sanity'
import {productBasePathForSite, productTypeCanonicalPathForSite} from '@/lib/productCanonical'

const localeFieldMap: Record<string, string> = {
  cs: 'csCZ',
  'cs-CZ': 'csCZ',
  da: 'daDK',
  'da-DK': 'daDK',
  de: 'deDE',
  'de-DE': 'deDE',
  en: 'enGB',
  'en-CA': 'enCA',
  'en-GB': 'enGB',
  'en-US': 'enUS',
  es: 'esES',
  'es-ES': 'esES',
  fi: 'fiFI',
  'fi-FI': 'fiFI',
  'fr-CA': 'frCA',
  'fr-FR': 'frFR',
  fr: 'frFR',
  it: 'itIT',
  'it-IT': 'itIT',
  nb: 'nbNO',
  'nb-NO': 'nbNO',
  nl: 'nlNL',
  'nl-NL': 'nlNL',
  pl: 'plPL',
  'pl-PL': 'plPL',
  sv: 'svSE',
  'sv-SE': 'svSE',
}

export const slugifyProductTypeLabel = (value: string): string =>
  value
    .replace(/[Ææ]/g, 'ae')
    .replace(/[Øø]/g, 'o')
    .replace(/[Åå]/g, 'a')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')

const labelFromLocalizedSlug = (value?: string | null): string | undefined => {
  const label = String(value || '')
    .trim()
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')

  if (!label) return undefined

  return label.charAt(0).toLocaleUpperCase() + label.slice(1)
}

export const localizedProductTypeLabel = (
  productType: ProductTypeCategoryDocument | SiteAwareProductTypeCategoryDocument,
  locale?: string,
): string | undefined => {
  const fieldName = locale ? localeFieldMap[locale] : undefined
  const labels = productType.labels

  if (fieldName && labels?.[fieldName as keyof typeof labels]) {
    return labels[fieldName as keyof typeof labels] || undefined
  }

  if (
    fieldName &&
    productType.slugsByLocale?.[fieldName as keyof typeof productType.slugsByLocale]
  ) {
    return labelFromLocalizedSlug(
      productType.slugsByLocale[fieldName as keyof typeof productType.slugsByLocale],
    )
  }

  const preferredFields = ['enGB', 'enUS', 'nbNO', 'svSE'] as const
  for (const field of preferredFields) {
    if (labels?.[field]) return labels[field] || undefined
  }

  return productType.title || undefined
}

export const productTypeSlugForSite = (
  productType: ProductTypeCategoryDocument | SiteAwareProductTypeCategoryDocument,
  site: SiteLite,
  locale?: string,
): string =>
  slugifyProductTypeLabel(
    localizedProductTypeLabel(productType, locale || site.locale) || productType.key || '',
  )

export const productTypePathForSite = (
  productType: ProductTypeCategoryDocument | SiteAwareProductTypeCategoryDocument,
  site: SiteLite,
  canonicalSettings?: CanonicalSettingsDocument | null,
  locale?: string,
): string => productTypeCanonicalPathForSite(productType, site, canonicalSettings, locale)

export const productTypeListingHref = (
  productType: ProductTypeCategoryDocument | SiteAwareProductTypeCategoryDocument,
  site: SiteLite,
  canonicalSettings?: CanonicalSettingsDocument | null,
): string => {
  const base = productBasePathForSite(site, canonicalSettings)
  const key = productType.key || ''
  return key ? `${base}?productType=${encodeURIComponent(key)}` : base
}
